'use client';

import { useEffect, useRef } from 'react';
import { CampaignLink } from '@/components/CampaignLink';
import SmartImage from './SmartImage';
import { MenuCategory } from '@/models/MenuCategories';

export function CategoryIconStrip({ categories }: { categories: MenuCategory[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);
  const speedRef = useRef(0.75);
  const scrollLeftRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const halfWidth = el.scrollWidth / 2;
    scrollLeftRef.current = el.scrollLeft;

    const syncScrollLeft = () => {
      if (isProgrammaticScrollRef.current) {
        isProgrammaticScrollRef.current = false;
        return;
      }

      scrollLeftRef.current = el.scrollLeft;
    };

    el.addEventListener('scroll', syncScrollLeft, { passive: true });

    const tick = () => {
      if (!isPausedRef.current && el) {
        scrollLeftRef.current += speedRef.current;

        if (scrollLeftRef.current >= halfWidth) {
          scrollLeftRef.current = 0;
        }

        isProgrammaticScrollRef.current = true;
        el.scrollLeft = scrollLeftRef.current;
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      el.removeEventListener('scroll', syncScrollLeft);
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    };
  }, [categories]);

  if (!categories.length) return null;

  const doubled = [...categories, ...categories];

  return (
    <div
      className="relative w-full py-1 overflow-hidden min-h-[72px]"
      onMouseEnter={() => {
        isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
      }}
    >
      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden flex gap-1 px-3 py-3 scrollbar-hide overscroll-x-contain"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {doubled.map((cat, i) => (
          <CampaignLink
            href={`/${cat.titleWD}`}
            key={`${cat.id}-${i}`}
            className="flex flex-row items-center gap-2 shrink-0 group bg-hzc_beige_100 hover:bg-hzc_beige_200 transition-colors rounded-xl px-3 py-2"
            aria-hidden={i >= categories.length}
          >
            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-hzc_beige_300 relative shrink-0 transition-transform group-hover:scale-105">
              <SmartImage
                fill
                sizes="40px"
                src={cat.menuIconUrl || ''}
                alt={cat.title}
                className="object-cover"
              />
            </div>
            <span className="text-[12px] text-hzc_purple_500 font-medium whitespace-nowrap">
              {cat.title}
            </span>
          </CampaignLink>
        ))}
      </div>
    </div>
  );
}
